import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { setActions } from 'actions/actionRolesSlice';

import Loading from 'components/global/Loading';

import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useChangeLanguage from 'hooks/useChangeLanguage';
import useLocale from 'hooks/useLocale';

import { setPrintLocale } from 'pages/Settings/Print/PrintSettingsSlice';

import axios, { setAuthorizationToken } from 'services/axios';
import { AUTH, COMPANY, ONBOARDING, PROFILE, USERS } from 'services/consts';

import { setInstructionData } from 'stores/InstructionSlice';
import { setRoles } from 'stores/systemRolesSlice';
import { setUserDetails, setUserInitialized } from 'stores/userSlice';

import AppLogger from './AppLogger';
import { logout } from './configureStore';
import { languages } from './localization';
import PATHS from './paths';

const UserService = (props) => {
	const dispatch = useDispatch();
	const { identify, reset } = useAppTracking();
	const changeLanguage = useChangeLanguage();
	const [searchParams] = useSearchParams();

	const axiosPrivate = useAxiosPrivate();
	const ACCESS_TOKEN = localStorage.getItem('ACCESS_TOKEN');
	const { showError } = useAlert();
	const isUserInitialized = useSelector((state) => state.user.initialized);
	const navigate = useNavigate();
	const { LanguageCode } = useLocale();

	const handleResetPassword = async () => {
		let link;
		try {
			const response = await axios.post(USERS.GET_RESET_PASSWORD_LINK);
			link = response.data.link;
			await axios.post(AUTH.LOGOUT);
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
		setAuthorizationToken();
		reset();
		dispatch(logout())
			.unwrap()
			.then(async () => {
				try {
					window.location = `${link}&force=true`;
				} catch (error) {
					AppLogger.error(error);
					showError();
				}
			});
	};

	useEffect(() => {
		const getUserDetails = async () => {
			Promise.all([
				axiosPrivate.post(PROFILE.GET_ROLES),
				axiosPrivate.post(PROFILE.GET_USER_DETAILS),
				axiosPrivate.post(COMPANY.GET_GENERAL_SETTINGS),
				axiosPrivate.post(ONBOARDING.GET_ONBOARDING)
			])
				.then(
					([
						{ data: roleData, status: roleStatus },
						{ data: userData, status: userStatus },
						{ data: generalSettingsData, status: generalSettingsStatus },
						{ data: onboardingData, status: onboardingStatus }
					]) => {
						if (
							roleStatus === 200 &&
							userStatus === 200 &&
							generalSettingsStatus === 200 &&
							(onboardingStatus === 200 || onboardingStatus === 204)
						) {
							if (roleData?.length === 0 || roleData === undefined) {
								showError({
									message: 'alert.noRoles'
								});
								navigate(PATHS.LOGOUT);
								return;
							}

							// If user has reset cache flag, clear local storage and redirect to logout
							if (userData.resetCache) {
								localStorage.clear();
								axiosPrivate.post(PROFILE.UPDATE_USER_DETAILS, {
									resetCache: false
								});
								navigate(PATHS.LOGOUT);
								return;
							}

							// If user has reset password flag, redirect to reset password
							if (
								userData.resetPassword &&
								!userData.roles?.map((roleObj) => roleObj.roleID).includes(91)
							) {
								handleResetPassword();
								return;
							}

							// Set user roles
							dispatch(
								setRoles([
									userData.planName,
									userData.userType,
									...roleData.filter(
										(role) => !(role.startsWith('/actions/') || role.startsWith('/rowActions/'))
									)
								])
							);
							dispatch(
								setActions(
									roleData.filter(
										(role) => role.startsWith('/actions/') || role.startsWith('/rowActions/')
									)
								)
							);

							// Set user details
							dispatch(
								setUserDetails({
									...userData,
									generalSettings: generalSettingsData
								})
							);

							dispatch(setPrintLocale(LanguageCode));

							// Init App Tracking
							identify({
								...userData,
								...onboardingData,
								...generalSettingsData,
								modules: roleData?.filter((role) => !role.includes('/'))
							});

							// Set onboarding data
							dispatch(setInstructionData(onboardingData));
							const otoToken = searchParams.get('oto-token');
							const languageCode = searchParams.get('language');
							const lang = otoToken && languageCode ? languageCode : userData.locale;
							// Set language
							changeLanguage(languages.find((language) => language.systemCode === lang).code).then(() => {
								// Set user initialized
								dispatch(setUserInitialized(true));
							});
						} else {
							AppLogger.error(
								'Error fetching login data: ',
								' roleStatus: ',
								roleStatus,
								' userData: ',
								userData,
								' userStatus: ',
								userStatus,
								' generalSettingsData: ',
								generalSettingsData,
								' generalSettingsStatus: ',
								generalSettingsStatus,
								' onboardingStatus: ',
								onboardingStatus
							);
							showError();
							navigate(PATHS.LOGOUT);
						}
					}
				)
				.catch((error) => {
					AppLogger.error(error);
					showError();
					navigate(PATHS.LOGOUT);
				});
		};

		if (ACCESS_TOKEN) {
			getUserDetails();
		}
	}, [dispatch]);

	return isUserInitialized ? props.children : <Loading />;
};

export default UserService;
